import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  PlusCircle,
  Settings,
  FileBarChart,
  SlidersHorizontal,
  Database,
  ArrowRightLeft,
  Workflow,
  LogOut,
  FileCode2,
  Scale,
  Bot,
} from 'lucide-react';
import { useAuth } from '../../auth';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard size={18} />, end: true },
  { to: '/strategy', label: 'Migration Strategy', icon: <Workflow size={18} /> },
  { to: '/migration/new', label: 'Parallel Migration', icon: <PlusCircle size={18} /> },
  { to: '/extraction', label: 'Extraction Setup', icon: <FileCode2 size={18} /> },
  { to: '/configuration', label: 'Reference Data', icon: <Database size={18} /> },
  { to: '/reconciliation', label: 'Reconciliation', icon: <Scale size={18} /> },
  { to: '/assistant', label: 'Assistant', icon: <Bot size={18} /> },
  { to: '/reports', label: 'Reports', icon: <FileBarChart size={18} /> },
  { to: '/settings', label: 'Setup', icon: <Settings size={18} /> },
];

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  return (
    <aside className="w-64 min-h-screen bg-[#1e3a5f] flex flex-col shadow-xl">
      {/* Logo / Brand */}
      <div className="px-5 py-6 border-b border-[#162c4a]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-400 bg-opacity-20 flex items-center justify-center">
            <ArrowRightLeft size={20} className="text-blue-300" />
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-tight">Whitestone ETL</p>
            <p className="text-blue-300 text-xs">Migration Platform</p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs text-blue-300 bg-blue-900 bg-opacity-30 rounded-lg px-3 py-2">
          <Database size={12} />
          <span className="truncate">Finacle 10.2.18 → 11.15</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        <p className="text-blue-400 text-xs font-semibold uppercase tracking-wider px-3 mb-3">
          Navigation
        </p>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-blue-200 hover:bg-[#162c4a] hover:text-white'
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-[#162c4a] space-y-2">
        {user && (
          <div className="px-2 text-xs text-blue-200 truncate">
            {user.name} · <span className="text-blue-400">{user.role}</span>
          </div>
        )}
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-blue-200 hover:bg-[#162c4a] hover:text-white transition-all"
        >
          <LogOut size={16} /> Sign out
        </button>
        <p className="text-blue-500 text-xs text-center">Whitestone Software · v1.0.0</p>
      </div>
    </aside>
  );
};

export default Sidebar;
